import { SecondaryMenuTrigger, DropDownSection, NumInputBoxTwoCol, SecondaryMenu, SecondaryMenuSectionTitle } from '../../../component';
import { useTranslation } from 'react-i18next';
import { DEFAULT_BLOCK_BACKGROUND_COLOR, DEFAULT_BLOCK_BORDER_COLOR } from '../../../../shared/documentColorDefaults';
import Stack from '@mui/material/Stack';
import { getBlockSidesFromString } from '../../../../GridDndEditor/Block/Helper/BlockSides';

import {
  EditorDefaultSidePanelType,
  setActiveSidePanel,
  setIsAdvancedSpacingModelOpen,
} from '../../../../grid/reduxStore/blockStyleSettingsSlice';
import { useNewBlockStyleSettings } from '../../../../hooks/useNewBlockSettings';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import { useCircularShape } from './useCircularShape';
import { SelectionContext } from 'components/editor/GridDndEditor/SelectedBlockInfoProvider';
import { useCallback, useContext } from 'react';
import { useBlockContentWithSectionId } from 'components/editor/Sections/useBlockContent';
import { useSelector } from 'react-redux';
import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';
import { ColorPickerWithUndoRedoSection } from '../../../component/ColorPickerWithundoRedoSection.tsx';

export function TextWithUndoRedoBlockSettings() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const sectionId = useSelector((state: RootState) => selectSectionIdByBlockId(state, blockId));
  const blockContent = useBlockContentWithSectionId(sectionId ?? '', blockId) as BlockContent;
  const { wouldBeCircular } = useCircularShape(blockContent);
  const newBlockStyleSettings = useNewBlockStyleSettings(blockId, sectionId ?? '');

  const {
    getBlockStyle,
    updateBlockStyleInUi,
    changeBlockBorderSide,
    updateBlockStyleWithUndo,
    saveCurrentStateToRef,
    changeBorderWidth,
    changeBorderRadiusInUi,
    changeBorderWidthInUi,
    changeBorderRadius,
  } = newBlockStyleSettings;

  const {
    backgroundColor = DEFAULT_BLOCK_BACKGROUND_COLOR,
    borderColor = DEFAULT_BLOCK_BORDER_COLOR,
    borderRadius = 0,
    currentBorderSide = [],
    borderWidth = 0,
  } = getBlockStyle(blockId);

  const handleBorderSideChange = useCallback(
    (borderSide: string[]) => {
      const sides = getBlockSidesFromString(borderSide);
      if (JSON.stringify(sides) !== JSON.stringify(currentBorderSide)) {
        changeBlockBorderSide(sides);
      }
    },
    [currentBorderSide, changeBlockBorderSide]
  );

  const handleFocus = useCallback(() => {
    saveCurrentStateToRef(getBlockStyle(blockId));
  }, [getBlockStyle, saveCurrentStateToRef]);

  const handleBackButtonClick = () => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  };

  const handleBorderRadiusChange = (radius: number) => {
    const isCircularShape = radius > 0 && wouldBeCircular(radius);
    changeBorderRadius({ radius, toAddPadding: !isCircularShape });
  };

  const handleBorderRadiusChangeinUi = (radius: number) => {
    const isCircularShape = radius > 0 && wouldBeCircular(radius);
    changeBorderRadiusInUi({ radius, toAddPadding: !isCircularShape });
  };

  return (
    <SecondaryMenu
      testId={'textbox-settings-component'}
      menuTitle={t('document.grid.text.design.title')}
      backButtonOnClick={handleBackButtonClick}
    >
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.text.design.background')}</SecondaryMenuSectionTitle>
        <ColorPickerWithUndoRedoSection
          currentColor={backgroundColor}
          onOpen={handleFocus}
          onColorChange={(backgroundColor) => updateBlockStyleInUi({ backgroundColor })}
          onClose={(backgroundColor: string) => updateBlockStyleWithUndo({ backgroundColor })}
        />
      </Stack>
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.text.design.border')}</SecondaryMenuSectionTitle>
        <ColorPickerWithUndoRedoSection
          currentColor={borderColor}
          onOpen={handleFocus}
          onColorChange={(borderColor) => updateBlockStyleInUi({ borderColor })}
          onClose={(borderColor: string) => updateBlockStyleWithUndo({ borderColor })}
        />
        <DropDownSection currentSelection={currentBorderSide} handleSelectionChange={handleBorderSideChange} testId={'border-dropdown'} />
        <NumInputBoxTwoCol
          inputValue={borderWidth}
          onChange={(borderWidth: number) => changeBorderWidthInUi(borderWidth)}
          onBlur={(borderWidth: number) => changeBorderWidth(borderWidth)}
          onFocus={handleFocus}
          title={t('document.grid.text.design.border_width')}
          testId={'border-width'}
          disableNegativeNum
        />
        <NumInputBoxTwoCol
          title={t('document.grid.text.design.border_radius')}
          inputValue={borderRadius}
          disableNegativeNum
          onFocus={handleFocus}
          onChange={(borderRadius) => handleBorderRadiusChangeinUi(borderRadius)}
          onBlur={(borderRadius) => handleBorderRadiusChange(borderRadius)}
          testId={'border-radius'}
        />
      </Stack>
      <SecondaryMenuTrigger
        ButtonText={t('document.grid.text.advanced_spacing.title')}
        ButtonDesc={t('document.grid.text.advanced_spacing.adjust_left_top_right_bottom_block_padding')}
        handleClick={() => dispatch(setIsAdvancedSpacingModelOpen(true))}
      />
    </SecondaryMenu>
  );
}
